import { z } from 'zod';
import type { AlertChipView, ShipmentListItem } from '@/lib/api/shipments';

// S01a · «Da guardare» (07 S01a): the attention shipments grouped by who acts
// (data · manual · support · none), by severity, or by carrier. A shipment
// goes in the group of its first open alert (the API lists blocking first).

export const GROUPINGS = ['actor', 'severity', 'carrier'] as const;
export type Grouping = (typeof GROUPINGS)[number];

export const attentionSearchSchema = z.object({
  by: z.enum(['severity', 'carrier']).optional().catch(undefined),
});
export type AttentionSearch = z.infer<typeof attentionSearchSchema>;

const ACTOR_ORDER = ['data', 'manual', 'support', 'none'] as const;
const SEVERITY_ORDER = ['blocking', 'warning', 'info'] as const;
const SEVERITY_RANK: Record<string, number> = { blocking: 0, warning: 1, info: 2 };

export interface AttentionGroup {
  key: string;
  items: ShipmentListItem[];
}

/** The alert a shipment is grouped and described by: the most severe open one. */
export function leadAlert(item: ShipmentListItem): AlertChipView | null {
  const open = item.alerts.filter((a) => !a.acknowledged_at);
  const list = open.length ? open : item.alerts;
  return (
    [...list].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3))[0] ?? null
  );
}

function keyOf(item: ShipmentListItem, by: Grouping): string {
  const lead = leadAlert(item);
  if (by === 'actor') return lead?.actor ?? 'none';
  if (by === 'severity') return lead?.severity ?? 'info';
  return item.carrier?.code ?? item.carrier_code ?? item.carrier_code_raw ?? '—';
}

export function groupAttention(items: ShipmentListItem[], by: Grouping): AttentionGroup[] {
  const groups = new Map<string, ShipmentListItem[]>();
  for (const item of items) {
    if (!item.attention) continue;
    const key = keyOf(item, by);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  const order: readonly string[] =
    by === 'actor' ? ACTOR_ORDER : by === 'severity' ? SEVERITY_ORDER : [...groups.keys()].sort();
  return order.filter((k) => groups.has(k)).map((key) => ({ key, items: groups.get(key) ?? [] }));
}
